import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      api.get('/auth/me').then(r => setUser(r.data)).catch(() => logout()).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  // Silent refresh on 401
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      res => res,
      async err => {
        const orig = err.config;
        if (err.response?.status === 401 && !orig._retry && !orig.url?.includes('/auth/')) {
          orig._retry = true;
          const rt = localStorage.getItem('refresh_token');
          if (rt) {
            try {
              const res = await api.post('/auth/refresh', { refresh_token: rt });
              const { token: newToken, refresh_token: newRefresh } = res.data;
              localStorage.setItem('token', newToken);
              localStorage.setItem('refresh_token', newRefresh);
              api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
              orig.headers['Authorization'] = `Bearer ${newToken}`;
              return api(orig);
            } catch {
              logout();
            }
          } else {
            logout();
          }
        }
        return Promise.reject(err);
      }
    );
    return () => api.interceptors.response.eject(interceptor);
  }, []);

  const login = (newToken, userData, refreshToken) => {
    localStorage.setItem('token', newToken);
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
  };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    delete api.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  const isAdmin = () => ['admin'].includes(user?.role);
  const isEditor = () => ['admin', 'editor'].includes(user?.role);
  const isPublisher = () => ['admin', 'editor', 'publisher', 'consul', 'consular_agent', 'financial'].includes(user?.role);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, isAdmin, isEditor, isPublisher }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
